// frontend/app/components/lessonPlans/blocks/VoiceAnswerControl.js
//
// "Speak your answer" for any free-text Loop block.
//
// The Learning Loop asks students to *explain*, and a 14-year-old will say
// more in thirty seconds than they will type in three minutes. Explain is also
// the heaviest evidence in the mastery model, so lowering the cost of
// producing an answer directly improves what the model has to work with.
//
// Three rules this component keeps:
//
//   1. Typing is never taken away. This renders *beside* the text field, never
//      instead of it, and the field stays editable throughout.
//   2. A transcript is APPENDED, never substituted. A student who typed two
//      sentences and then spoke a third keeps all three.
//   3. Where speaking is impossible — no microphone, denied permission, a
//      native build with no MediaRecorder — nothing renders at all. An
//      affordance that cannot work is worse than no affordance.

import React, { useCallback } from "react";
import { ActivityIndicator, StyleSheet, Text, View } from "react-native";

import useVoiceAnswer from "../useVoiceAnswer";
import { ActionButton } from "./LessonBlockShell";

/**
 * @param {string}   value      current text, so a transcript can be appended
 * @param {function} onChange   (nextText) => void
 * @param {function} onEvent    optional exploration-event sink
 */
const VoiceAnswerControl = ({
  colors,
  accent,
  value = "",
  onChange,
  readOnly = false,
  language = "en",
  onEvent,
  t,
}) => {
  const translate = t || ((s) => s);

  const handleTranscript = useCallback(
    (text) => {
      const existing = (value || "").trim();
      onChange?.(existing ? `${existing} ${text}` : text);
    },
    [value, onChange]
  );

  const { supported, recording, transcribing, error, start, stop } =
    useVoiceAnswer({ onTranscript: handleTranscript, language, onEvent });

  // Nothing to offer: fall through to typing with no dead button on screen.
  if (!supported || readOnly) return null;

  return (
    <View style={styles.wrap}>
      <View style={styles.row}>
        {transcribing ? (
          <View style={styles.busy}>
            <ActivityIndicator size="small" color={accent || colors.accentPrimary} />
            <Text style={[styles.busyText, { color: colors.textSecondary }]}>
              {translate("Turning your words into text…")}
            </Text>
          </View>
        ) : (
          <ActionButton
            colors={colors}
            label={
              recording
                ? translate("Stop and use this")
                : translate("Speak your answer")
            }
            icon={recording ? "stop-circle-outline" : "microphone-outline"}
            onPress={recording ? stop : start}
            accessibilityLabel={
              recording
                ? translate("Stop recording and transcribe")
                : translate("Speak your answer instead of typing")
            }
          />
        )}

        {recording ? (
          <Text style={[styles.hint, { color: colors.danger }]}>
            {translate("Recording — speak as if explaining to a classmate.")}
          </Text>
        ) : !transcribing ? (
          <Text style={[styles.hint, { color: colors.textMuted }]}>
            {translate("Or type below.")}
          </Text>
        ) : null}
      </View>

      {/* A failed recording is never a dead end: the message says what still
          works rather than only what went wrong. */}
      {error ? (
        <Text style={[styles.error, { color: colors.warning }]}>{error}</Text>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: { marginBottom: 8, gap: 4 },
  row: { flexDirection: "row", alignItems: "center", gap: 10, flexWrap: "wrap" },
  busy: { flexDirection: "row", alignItems: "center", gap: 8 },
  busyText: { fontSize: 13 },
  hint: { fontSize: 12, flexShrink: 1 },
  error: { fontSize: 12 },
});

export default VoiceAnswerControl;
